import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AlertLevelEnum } from './alert-level.enum';
import { UploadTypesEnum } from './upload-types.enum';
import { ResourceTypesEnum } from './resource-types.enum';
import { ResourcePathsEnum } from './resource-paths.enum';
import { WorksheetParsedRow } from '../web-workers/spreadsheet-parser-worksheet';
import { IsAdminService } from '../../../services/admin';

export interface HeaderColumn {
  columnHeader: string;
  mapFieldName: string | null;
}

export interface HeaderColumnsRow {
  headerColumns: Array<HeaderColumn>;
  rowIndex: number;
  values: []; // Always empty array?
}

export interface GetHeaderRowApiResponse {
  headerIndex: number;
  rows: Array<HeaderColumnsRow>;
}

export interface AssignableField {
  dataType: 'STRING' | 'NUMBER' | 'ENUM';
  displayName: string;
  displayOrder: number;
  name: string;
  required: boolean;
  uploadType: string;
  version: number;
}

export interface AssignedField {
  columnHeader: string;
  mapFieldName: string;
}

// TODO: fix interface CQVA-501
export interface GetAssignableFieldsApiResponse {
  [index: number]: Array<AssignableField>;
}

export interface UploadContractApiResponse {
  isSuccess: boolean;
  errorMessages: Array<string>;
}

export interface UploadFileApiResponse {
  id: number;
  name: string;
}

export interface AnalysisViolation {
  alertLevel:
    | AlertLevelEnum.HIGH
    | AlertLevelEnum.MEDIUM
    | AlertLevelEnum.LOW
    | null;
  badge: string | null;
  message: string;
  violationCount: number;
}

export interface GetAnalysisApiResponse {
  aggregations: unknown;
  downloadUrl: string;
  products: Array<unknown>;
  recordMatchedCount: number;
  recordViolationCount: number;
  analysisViolations: Array<AnalysisViolation>;
  /** @description The count of violations that can be submitted for review.  */
  sendForReviewCount: number;
  recordDeletionCount: number;
}

export interface ReviewRequestResponse {
  sentForReviewCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class UploadRequestsApiService {
  get baseUrl() {
    if (this.isAdminService.isAdmin) {
      return `/apt-admin/api/uploadrequests`;
    }

    return `/aptitude/api/uploadrequests`;
  }

  constructor(
    private http: HttpClient,
    private isAdminService: IsAdminService,
  ) {}

  uploadFiles(
    files: File[], // Taking array but only supporting one file for now.
    resourceData: {
      uploadType: UploadTypesEnum;
      resourceType: ResourceTypesEnum;
      resourceId: number;
      resourcePath?: ResourcePathsEnum | string;
    },
  ): Observable<Array<UploadFileApiResponse>> {
    // This API only supports formData content type so we're creating a form data object.
    const formData = new FormData();
    formData.append('files', files[0], files[0].name);
    formData.append('uploadType', resourceData.uploadType);
    formData.append('resourceType', resourceData.resourceType);
    formData.append('resourceId', resourceData.resourceId.toString());
    formData.append('resourcePath', resourceData.resourcePath || '');

    return this.http.post(this.baseUrl, formData) as Observable<
      Array<UploadFileApiResponse>
    >;
  }

  getAssignableFields(fileId: string): Observable<Array<AssignableField>> {
    return this.http.get(
      `${this.baseUrl}/${fileId}/assignablefields`,
    ) as Observable<Array<AssignableField>>;
  }

  getHeaderRow(
    fileId: string,
    rows: Array<WorksheetParsedRow>,
  ): Observable<GetHeaderRowApiResponse> {
    return this.http.post(`${this.baseUrl}/${fileId}/header`, {
      rows,
    }) as Observable<GetHeaderRowApiResponse>;
  }

  getAnalysis(
    fileUploadId: string,
    headerIndex: number,
    sheetName: string,
    fields: Array<AssignedField>,
  ) {
    return this.http.post(`${this.baseUrl}/${fileUploadId}/analysisrequests`, {
      fileUploadId,
      headerIndex,
      sheetName,
      fields,
    }) as Observable<GetAnalysisApiResponse>;
  }

  submitReviewRequest(fileUploadId: string): Observable<ReviewRequestResponse> {
    return this.http.post(`${this.baseUrl}/${fileUploadId}/reviewrequests`, {
      uploadRequestId: fileUploadId,
    }) as Observable<ReviewRequestResponse>;
  }
}
